import React, { useState } from 'react';
import { Link } from 'react-scroll';
import { 
  Shield, MessageSquare, FileText, CreditCard, BarChart, 
  ChevronRight, Settings, Users, Building, Briefcase, Link2,
  Rocket, Database, Menu, X
} from 'lucide-react';
import './Navbar.css';

const Navbar = () => {
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) {
      setIsProductsOpen(false);
      setIsSolutionsOpen(false);
      setIsServicesOpen(false);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsProductsOpen(false);
    setIsSolutionsOpen(false);
    setIsServicesOpen(false);
  };

  const handleMobileToggle = (menu) => {
    if (window.innerWidth <= 1024) {
      if (menu === 'products') {
        setIsProductsOpen(!isProductsOpen);
        setIsSolutionsOpen(false);
        setIsServicesOpen(false);
      } else if (menu === 'solutions') {
        setIsSolutionsOpen(!isSolutionsOpen);
        setIsProductsOpen(false);
        setIsServicesOpen(false);
      } else if (menu === 'services') {
        setIsServicesOpen(!isServicesOpen);
        setIsProductsOpen(false);
        setIsSolutionsOpen(false);
      }
    }
  };

  const handleDesktopEnter = (menu) => {
    if (window.innerWidth > 1024) {
      if (menu === 'products') setIsProductsOpen(true);
      if (menu === 'solutions') setIsSolutionsOpen(true);
      if (menu === 'services') setIsServicesOpen(true);
    }
  };

  const handleDesktopLeave = (menu) => {
    if (window.innerWidth > 1024) {
      if (menu === 'products') setIsProductsOpen(false);
      if (menu === 'solutions') setIsSolutionsOpen(false);
      if (menu === 'services') setIsServicesOpen(false);
    }
  };

  // Reusable scroll config for all links
  const scrollProps = {
    spy: true,
    smooth: 'easeInOutQuart', // Premium smooth easing curve
    offset: -80, // Accounts for navbar height
    duration: 800, // Premium smooth speed (ms)
    onClick: closeMobileMenu
  };

  return (
    <nav className="custom-navbar">
      <Link to="home" className="nav-logo" {...scrollProps} style={{cursor: 'pointer'}}>
        <img src="/logo.png" alt="Acurio" className="navbar-logo-img" />
        Acurio 
      </Link>
      
      <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
        {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
      </button>

      <div className={`nav-links ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        
        {/* HIGH-TECH SCROLL LINKS */}
        <Link activeClass="active" to="home" className="nav-link" {...scrollProps}>Home</Link>
        
        <div className="nav-item-dropdown" onMouseEnter={() => handleDesktopEnter('products')} onMouseLeave={() => handleDesktopLeave('products')}>
          <div className="nav-dropdown-trigger" onClick={() => handleMobileToggle('products')}>
            Products <span className="chevron">▾</span>
          </div>
          {isProductsOpen && (
            <div className="mega-menu">
              <div className="mega-menu-grid">
                <Link to="home" className="mega-card" {...scrollProps}>
                  <div className="mega-icon-wrapper"><Shield size={28} color="#4c1d95" /></div>
                  <div className="mega-card-content">
                    <h4>Kryptic Vault <ChevronRight size={16} color="#9ca3af"/></h4>
                    <p>Controlled credential proxy and encrypted storage</p>
                  </div>
                </Link>
                <Link to="home" className="mega-card" {...scrollProps}>
                  <div className="mega-icon-wrapper"><MessageSquare size={28} color="#0f766e" /></div>
                  <div className="mega-card-content">
                    <h4>Patient Comms <ChevronRight size={16} color="#9ca3af"/></h4>
                    <p>Omnichannel system with AI outbound calls</p>
                  </div>
                </Link>
                <Link to="home" className="mega-card" {...scrollProps}>
                  <div className="mega-icon-wrapper"><FileText size={28} color="#b45309" /></div>
                  <div className="mega-card-content">
                    <h4>Digital Intake <ChevronRight size={16} color="#9ca3af"/></h4>
                    <p>Dynamic builders generating structured payloads</p>
                  </div>
                </Link>
                <Link to="home" className="mega-card" {...scrollProps}>
                  <div className="mega-icon-wrapper"><CreditCard size={28} color="#be185d" /></div>
                  <div className="mega-card-content">
                    <h4>Patient AR <ChevronRight size={16} color="#9ca3af"/></h4>
                    <p>Track ledger entries and automate payments</p>
                  </div>
                </Link>
                <Link to="home" className="mega-card" {...scrollProps}>
                  <div className="mega-icon-wrapper"><BarChart size={28} color="#1d4ed8" /></div>
                  <div className="mega-card-content">
                    <h4>Analytics <ChevronRight size={16} color="#9ca3af"/></h4>
                    <p>Cross-module reporting and dashboards</p>
                  </div>
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="nav-item-dropdown" onMouseEnter={() => handleDesktopEnter('solutions')} onMouseLeave={() => handleDesktopLeave('solutions')}>
          <div className="nav-dropdown-trigger" onClick={() => handleMobileToggle('solutions')}>
            Solutions <span className="chevron">▾</span>
          </div>
          {isSolutionsOpen && (
            <div className="mega-menu mega-solutions">
              <div className="solutions-left">
                <div className="solutions-column">
                  <h4 className="solutions-heading">By Workflow</h4>
                  <Link to="home" className="solution-link" {...scrollProps}>
                    <div className="solution-icon"><CreditCard size={20} /></div>
                    <span>Revenue Cycle (AR & Insurance)</span>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        <Link activeClass="active" to="about" className="nav-link" {...scrollProps}>About</Link>
        <Link activeClass="active" to="contact" className="nav-link" {...scrollProps}>Contact Us</Link>
        
        <div className="mobile-nav-actions">
          <button className="nav-demo-btn">Request a Demo</button>
        </div>
      </div>

      <div className="desktop-nav-actions">
        <button className="nav-demo-btn">Request a Demo</button>
      </div>
    </nav>
  );
};

export default Navbar;